import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import SendIcon from "@material-ui/icons/Send";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const emptyFilters = {
  searchParam: "",
  dateFrom: "",
  dateTo: "",
  userId: "",
  stars: "",
  queueId: ""
};

const defaultCampaignMessage = `Queremos seguir mejorando para brindarle una mejor atención.

Su opinión nos ayuda a identificar qué estamos haciendo bien y qué podemos mejorar.

⭐ Califíquenos aquí:
{{link}}`;

export default function ServiceRatings() {
  const [activeTab, setActiveTab] = useState("rated");
  const [ratings, setRatings] = useState([]);
  const [ratingsCount, setRatingsCount] = useState(0);
  const [pendingRatings, setPendingRatings] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [filters, setFilters] = useState(emptyFilters);
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [selectedRating, setSelectedRating] = useState(null);
  const [selectedPendings, setSelectedPendings] = useState([]);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [campaignMessage, setCampaignMessage] = useState(defaultCampaignMessage);
  const [sendingCampaign, setSendingCampaign] = useState(false);

  const loadSupportData = async () => {
    try {
      const [{ data: usersResponse }, { data: queuesResponse }] = await Promise.all([
        api.get("/users"),
        api.get("/queue")
      ]);
      setUsers(Array.isArray(usersResponse?.users) ? usersResponse.users : []);
      setQueues(Array.isArray(queuesResponse) ? queuesResponse : Array.isArray(queuesResponse?.queues) ? queuesResponse.queues : []);
    } catch (err) {
      toastError(err);
    }
  };

  const loadRatings = async (all = false) => {
    try {
      const { data } = await api.get("/service-ratings", {
        params: {
          ...filters,
          all: all ? "true" : "false",
          pageNumber: 1,
          pageSize: all ? 10000 : 100
        }
      });
      if (all) {
        return Array.isArray(data?.ratings) ? data.ratings : [];
      }
      setRatings(Array.isArray(data?.ratings) ? data.ratings : []);
      setRatingsCount(Number(data?.count || 0));
      return [];
    } catch (err) {
      toastError(err);
      return [];
    }
  };

  const loadPendingRatings = async (all = false) => {
    try {
      const { data } = await api.get("/service-ratings/pending", {
        params: {
          searchParam: filters.searchParam,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          userId: filters.userId,
          queueId: filters.queueId,
          all: all ? "true" : "false",
          pageNumber: 1,
          pageSize: all ? 10000 : 100
        }
      });

      const pendings = Array.isArray(data?.pendings) ? data.pendings : [];
      if (all) {
        return pendings;
      }

      setPendingRatings(pendings);
      setPendingCount(Number(data?.count || pendings.length || 0));
      setSelectedPendings(prev => prev.filter(id => pendings.some(item => item.id === id)));
      return [];
    } catch (err) {
      toastError(err);
      return [];
    }
  };

  const loadActiveTab = async () => {
    if (activeTab === "pending") {
      await loadPendingRatings();
      return;
    }
    await loadRatings();
  };

  useEffect(() => {
    loadSupportData();
  }, []);

  useEffect(() => {
    loadActiveTab();
  }, [activeTab]);

  const handleExport = async () => {
    const exportRows = activeTab === "pending"
      ? await loadPendingRatings(true)
      : await loadRatings(true);

    const data = activeTab === "pending"
      ? exportRows.map(item => ({
          UltimoEnvio: item.requestedAt ? new Date(item.requestedAt).toLocaleString("es-CO") : "",
          Cliente: item.clientName,
          Telefono: item.clientNumber,
          Usuario: item.agentName,
          Correo: item.agentEmail,
          Cola: item.queueName,
          EstadoTicket: item.ticketStatus,
          Ticket: item.ticketId,
          Enlace: item.ratingLink
        }))
      : exportRows.map(item => ({
          Fecha: new Date(item.createdAt).toLocaleString("es-CO"),
          Cliente: item.clientName,
          Telefono: item.clientNumber,
          Usuario: item.agentName,
          Correo: item.agentEmail,
          Cola: item.queueName,
          Estrellas: item.stars,
          Puntaje: item.rate,
          Comentario: item.comment,
          Ticket: item.ticketId,
          IP: item.accessDetails?.ipAddress || "",
          Navegador: item.accessDetails?.browser || "",
          SistemaOperativo: item.accessDetails?.operatingSystem || "",
          Dispositivo: item.accessDetails?.deviceType || "",
          Idioma: item.accessDetails?.language || ""
        }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      activeTab === "pending" ? "PendientesCalificar" : "Calificaciones"
    );
    XLSX.writeFile(
      wb,
      activeTab === "pending" ? "pendientes-calificacion.xlsx" : "calificaciones-servicio.xlsx"
    );
  };

  const visiblePendingIds = useMemo(
    () => pendingRatings.map(item => item.id),
    [pendingRatings]
  );

  const allVisibleSelected = visiblePendingIds.length > 0 && visiblePendingIds.every(id => selectedPendings.includes(id));

  const handleTogglePending = id => {
    setSelectedPendings(prev => (
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    ));
  };

  const handleToggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedPendings(prev => prev.filter(id => !visiblePendingIds.includes(id)));
      return;
    }

    setSelectedPendings(prev => Array.from(new Set([...prev, ...visiblePendingIds])));
  };

  const handleOpenCampaignDialog = () => {
    if (!selectedPendings.length) {
      toast.info("Seleccione al menos un cliente pendiente para reenviar la campaña.");
      return;
    }
    setCampaignDialogOpen(true);
  };

  const handleSendCampaign = async () => {
    try {
      setSendingCampaign(true);
      const { data } = await api.post("/service-ratings/pending/send-campaign", {
        trackingIds: selectedPendings,
        messageTemplate: campaignMessage
      });

      toast.success(
        `Campaña enviada: ${Number(data?.sentCount || 0)} reenviado(s), ${Number(data?.skippedCount || 0)} omitido(s).`
      );
      setCampaignDialogOpen(false);
      setSelectedPendings([]);
      await loadPendingRatings();
    } catch (err) {
      toastError(err);
    } finally {
      setSendingCampaign(false);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Title>
              {activeTab === "pending"
                ? `Pendientes por calificar (${pendingCount})`
                : `Calificaciones (${ratingsCount})`}
            </Title>
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label={activeTab === "pending" ? "Buscar cliente o numero" : "Buscar cliente"}
                  variant="outlined"
                  size="small"
                  value={filters.searchParam}
                  onChange={e => setFilters(prev => ({ ...prev, searchParam: e.target.value }))}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  fullWidth
                  label="Desde"
                  type="date"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={filters.dateFrom}
                  onChange={e => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2}>
                <TextField
                  fullWidth
                  label="Hasta"
                  type="date"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={filters.dateTo}
                  onChange={e => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                />
              </Grid>
              {activeTab === "rated" && (
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel>Estrellas</InputLabel>
                    <Select
                      label="Estrellas"
                      value={filters.stars}
                      onChange={e => setFilters(prev => ({ ...prev, stars: e.target.value }))}
                    >
                      <MenuItem value="">Todas</MenuItem>
                      {[1, 2, 3, 4, 5].map(value => (
                        <MenuItem key={value} value={value}>{value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={activeTab === "rated" ? 3 : 5}>
                <Grid container spacing={1}>
                  <Grid item xs={activeTab === "pending" ? 4 : 6}>
                    <Button fullWidth variant="contained" color="primary" onClick={loadActiveTab}>
                      Filtrar
                    </Button>
                  </Grid>
                  <Grid item xs={activeTab === "pending" ? 4 : 6}>
                    <Button fullWidth variant="outlined" color="primary" onClick={handleExport}>
                      Exportar
                    </Button>
                  </Grid>
                  {activeTab === "pending" && (
                    <Grid item xs={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        startIcon={<SendIcon />}
                        onClick={handleOpenCampaignDialog}
                      >
                        Campaña
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>

      <Paper variant="outlined" style={{ marginBottom: 16, borderRadius: 14 }}>
        <Tabs
          value={activeTab}
          onChange={(_e, value) => setActiveTab(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab value="rated" label={`Calificadas (${ratingsCount})`} />
          <Tab value="pending" label={`Pendientes por calificar (${pendingCount})`} />
        </Tabs>
      </Paper>

      <Paper variant="outlined" style={{ padding: 16, marginBottom: 16 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Usuario</InputLabel>
              <Select
                label="Usuario"
                value={filters.userId}
                onChange={e => setFilters(prev => ({ ...prev, userId: e.target.value }))}
              >
                <MenuItem value="">Todos</MenuItem>
                {users.map(item => (
                  <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Cola</InputLabel>
              <Select
                label="Cola"
                value={filters.queueId}
                onChange={e => setFilters(prev => ({ ...prev, queueId: e.target.value }))}
              >
                <MenuItem value="">Todas</MenuItem>
                {queues.map(item => (
                  <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {activeTab === "rated" ? (
        <Paper variant="outlined" style={{ padding: 16, overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Cola</TableCell>
                <TableCell>Estrellas</TableCell>
                <TableCell>Comentario</TableCell>
                <TableCell align="center">Detalle</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ratings.map(item => (
                <TableRow key={item.id}>
                  <TableCell>{new Date(item.createdAt).toLocaleString("es-CO")}</TableCell>
                  <TableCell>
                    <strong>{item.clientName}</strong>
                    <div style={{ opacity: 0.7 }}>{item.clientNumber}</div>
                  </TableCell>
                  <TableCell>
                    <strong>{item.agentName}</strong>
                    <div style={{ opacity: 0.7 }}>{item.agentEmail}</div>
                  </TableCell>
                  <TableCell>{item.queueName}</TableCell>
                  <TableCell>{"★".repeat(item.stars)}{"☆".repeat(Math.max(0, 5 - item.stars))}</TableCell>
                  <TableCell style={{ minWidth: 280 }}>{item.comment || "Sin comentario"}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" color="primary" onClick={() => setSelectedRating(item)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!ratings.length && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="textSecondary">
                      No hay calificaciones con esos filtros.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      ) : (
        <Paper variant="outlined" style={{ padding: 16, overflowX: "auto" }}>
          <Grid container spacing={2} alignItems="center" style={{ marginBottom: 8 }}>
            <Grid item xs={12} md={8}>
              <Typography variant="body2" color="textSecondary">
                Seleccione los clientes que ya recibieron el enlace, pero todavía no han calificado.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} style={{ textAlign: "right" }}>
              <Chip
                color={selectedPendings.length ? "secondary" : "default"}
                label={`${selectedPendings.length} seleccionado(s)`}
              />
            </Grid>
          </Grid>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={allVisibleSelected}
                    indeterminate={selectedPendings.length > 0 && !allVisibleSelected}
                    onChange={handleToggleAllVisible}
                  />
                </TableCell>
                <TableCell>Ultimo envio</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Cola</TableCell>
                <TableCell>Estado ticket</TableCell>
                <TableCell>Enlace</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingRatings.map(item => (
                <TableRow key={item.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={selectedPendings.includes(item.id)}
                      onChange={() => handleTogglePending(item.id)}
                    />
                  </TableCell>
                  <TableCell>{item.requestedAt ? new Date(item.requestedAt).toLocaleString("es-CO") : "Sin fecha"}</TableCell>
                  <TableCell>
                    <strong>{item.clientName}</strong>
                    <div style={{ opacity: 0.7 }}>{item.clientNumber}</div>
                  </TableCell>
                  <TableCell>
                    <strong>{item.agentName}</strong>
                    <div style={{ opacity: 0.7 }}>{item.agentEmail}</div>
                  </TableCell>
                  <TableCell>{item.queueName || "Sin cola"}</TableCell>
                  <TableCell>{item.ticketStatus || "Sin estado"}</TableCell>
                  <TableCell style={{ minWidth: 260, wordBreak: "break-all" }}>
                    {item.ratingLink}
                  </TableCell>
                </TableRow>
              ))}
              {!pendingRatings.length && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="textSecondary">
                      No hay clientes pendientes por calificar con esos filtros.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog
        open={Boolean(selectedRating)}
        onClose={() => setSelectedRating(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Detalle tecnico de la calificacion</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="textSecondary">Cliente</Typography>
              <Typography variant="body2">{selectedRating?.clientName || "No disponible"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="textSecondary">Fecha y hora</Typography>
              <Typography variant="body2">
                {selectedRating?.accessDetails?.submittedAt
                  ? new Date(selectedRating.accessDetails.submittedAt).toLocaleString("es-CO")
                  : "No disponible"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="textSecondary">IP</Typography>
              <Typography variant="body2">{selectedRating?.accessDetails?.ipAddress || "No disponible"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="textSecondary">Dispositivo</Typography>
              <Typography variant="body2">{selectedRating?.accessDetails?.deviceType || "No identificado"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="textSecondary">Sistema operativo</Typography>
              <Typography variant="body2">{selectedRating?.accessDetails?.operatingSystem || "No identificado"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="textSecondary">Navegador</Typography>
              <Typography variant="body2">{selectedRating?.accessDetails?.browser || "No identificado"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="textSecondary">Idioma</Typography>
              <Typography variant="body2">{selectedRating?.accessDetails?.language || "No disponible"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="textSecondary">Origen</Typography>
              <Typography variant="body2">{selectedRating?.accessDetails?.referer || "Directo o no informado"}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">User-Agent completo</Typography>
              <Paper variant="outlined" style={{ padding: 12, marginTop: 6, background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="body2" style={{ wordBreak: "break-word" }}>
                  {selectedRating?.accessDetails?.userAgent || "No disponible"}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRating(null)} color="primary" variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={campaignDialogOpen}
        onClose={() => !sendingCampaign && setCampaignDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Enviar campaña de calificación</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Se reenviará el enlace de calificación a {selectedPendings.length} cliente(s). Use <strong>{"{{link}}"}</strong> dentro del mensaje si quiere controlar la posición exacta del enlace.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={8}
                variant="outlined"
                label="Mensaje de campaña"
                value={campaignMessage}
                onChange={e => setCampaignMessage(e.target.value)}
                helperText="Si no incluye {{link}}, el sistema lo agregará al final automáticamente."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignDialogOpen(false)} disabled={sendingCampaign}>
            Cancelar
          </Button>
          <Button
            onClick={handleSendCampaign}
            color="primary"
            variant="contained"
            disabled={sendingCampaign}
            startIcon={<SendIcon />}
          >
            {sendingCampaign ? "Enviando..." : "Enviar campaña"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
}
